import { useMemo } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { EngagementDto } from '@families-link/shared';
import { Typography } from '@mui/material';
import { tableCellTypographyOverflowStyles } from '../styles';
import { mergeSx } from 'merge-sx';
import { ImportedWarningsTableActions } from '../components/imported-warnings-table/imported-warnings-table-actions.component';
import { ImportedWarningsTableHandlers } from '../components/imported-warnings-table';
import { compact } from 'lodash';

const helper = createColumnHelper<{
	columnName: string;
	columnValue: string;
	rowIndex: number;
	engagementTitle: string;
	message: string;
	createdObject: any;
}>();

export type ImportedWarningsColumnName =
	| 'rowIndex'
	| 'engagementTitle'
	| 'columnName'
	| 'columnValue'
	| 'message'
	| 'actions';

export function useImportedWarningsTableColumns(
	handlers: ImportedWarningsTableHandlers,
	importedWarningsColumnsList: ImportedWarningsColumnName[]
): ColumnDef<any, string>[] {
	const { onEdit } = handlers;

	const [t] = useTranslation();
	const result = useMemo(
		() =>
			compact(
				importedWarningsColumnsList.map((el: ImportedWarningsColumnName) => {
					switch (el) {
						case 'rowIndex':
							return helper.accessor('rowIndex', {
								header: 'Row',
								meta: {
									flex: 0.5,
								},
								enableSorting: false,
								cell: ({ getValue, row }) => (
									<Typography sx={mergeSx(tableCellTypographyOverflowStyles)}>{getValue()}</Typography>
								),
							}) as ColumnDef<any, string>;
						case 'engagementTitle':
							return helper.accessor('engagementTitle', {
								header: t('engagements.columns.title'),
								meta: {
									flex: 1,
									useCustomOverflowContainer: true,
								},
								enableSorting: false,
								cell: ({ getValue }) => (
									<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
										{getValue()}
									</Typography>
								),
							}) as ColumnDef<any, string>;
						case 'columnName':
							return helper.accessor('columnName', {
								header: 'Imported column',
								meta: {
									flex: 1,
									useCustomOverflowContainer: true,
								},
								enableSorting: false,
								cell: ({ getValue }) => (
									<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
										{getValue()}
									</Typography>
								),
							}) as ColumnDef<any, string>;
						case 'columnValue':
							return helper.accessor('columnValue', {
								header: 'Imported value',
								meta: {
									flex: 1,
									useCustomOverflowContainer: true,
								},
								enableSorting: false,
								cell: ({ getValue }) => (
									<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
										{getValue() ? getValue() : '-'}
									</Typography>
								),
							}) as ColumnDef<any, string>;
						case 'message':
							return helper.accessor('message', {
								header: 'Message',
								meta: {
									flex: 1,
									useCustomOverflowContainer: true,
								},
								enableSorting: false,
								cell: ({ getValue }) => (
									<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
										{getValue()}
									</Typography>
								),
							}) as ColumnDef<any, string>;
						case 'actions':
							return helper.display({
								id: 'actions',
								meta: {
									flex: 1,
								},
								cell: (props) => (
									<ImportedWarningsTableActions dto={props.row.original.createdObject} onEdit={onEdit} />
								),
							}) as ColumnDef<any>;

						default:
							return undefined;
					}
				})
			),
		[t, onEdit]
	);

	return result;
}
